import PropTypes from "prop-types";
import React from "react";

// @material-ui/core components
import { makeStyles, withStyles } from "@mui/styles";

import FormControlLabel from "@mui/material/FormControlLabel";

import MuiCheckbox from "@mui/material/Checkbox";
import { Field, Form, Formik } from "formik";
import { fieldToCheckbox } from "formik-mui";
import * as Yup from "yup";

import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";

// @material-ui/icons
import Check from "@mui/icons-material/Check";

// core components
import {
  Button,
  Card,
  CardBody,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import { FormikTextField, Headline, TextMaskPhone } from "./shared.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import { kickBlue } from "~/shared/styles/baseStyles.js";

import {
  COLOR_KICK_BLUE,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  errorMessages,
} from "~/shared/sharedConstants.js";

import {
  identifyCustomerIOPatientSignUp,
  identifyKlaviyoPatientSignUp,
  trackSignup,
} from "~/shared/utils/analytics";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import { trackConsultStart } from "../utils/analytics";
import KickClient from "../utils/api.js";

import {
  getVisitAPIToken,
  getVisitEmail,
  getVisitUSState,
  setVisitGender,
  setVisitPhone,
} from "../utils/visitCookies.js";

const useConsultStyles = makeStyles(consultationStyles);

const genders = [
  {
    value: "M",
    label: "Male",
  },
  {
    value: "F",
    label: "Female",
  },
  {
    value: "X",
    label: "Unspecified or Other",
  },
];

const phoneRegEx = /((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}/;
const phoneRegExStart0 = /^\(([^0])/;
const phoneRegExStart1 = /^\(([^1])/;
const phoneRegExAreaCode555 = /^\((?!555)/;

const initialValues = {
  gender: "",
  phone: "",
  profession: "",
  consent: true,
};

const TextFieldPhoneNumber = (props) => {
  return (
    <FormikTextField
      fullWidth
      {...props}
      id="phone"
      InputProps={{
        inputComponent: TextMaskPhone,
      }}
    />
  );
};

const TextFieldGender = (props) => {
  return (
    <FormikTextField
      defaultValue=""
      fullWidth
      {...props}
      id="gender"
      label={props.field.value ? "" : "Choose..."} // hide label if value is selected
      InputLabelProps={{ shrink: false }}
    />
  );
};

const ConsentCheckbox = (props) => {
  const classes = useConsultStyles();
  const checkboxProps = fieldToCheckbox(props);

  const {
    form: { setFieldValue },
    field: { name },
  } = props;
  const onClick = React.useCallback(
    (event) => {
      const { value } = event.target;
      setFieldValue(name, value ? name : null);
    },
    [setFieldValue, name],
  );

  return (
    <React.Fragment>
      <FormControlLabel
        style={{
          marginTop: "20px",
          marginBottom: "10px",
          width: "90%",
          justifyContent: "center",
        }}
        control={
          <MuiCheckbox
            checked
            {...checkboxProps}
            onClick={onClick}
            classes={{
              checked: classes.checked,
              root: classes.checkRoot,
            }}
          />
        }
        label={
          <span style={{ fontSize: "12px" }}>
            I agree to{" "}
            <a
              href="/telehealth-consent"
              target="_blank"
              style={{ textDecoration: "underline" }}
            >
              Telehealth Consent
            </a>{" "}
            <span>&</span>{" "}
            <a
              href="https://cdn-static.gokick.com/medical/KickHealthNoticeOfPrivacyPractices.pdf"
              target="_blank"
              style={{ textDecoration: "underline" }}
              rel="noreferrer"
            >
              Notice of Privacy Practices
            </a>
          </span>
        }
      />
      <FormHelperText error style={{ textAlign: "center", marginTop: "-2px" }}>
        {props.form.errors[props.field.name] != null
          ? "Please agree and click the checkbox to continue"
          : ""}
      </FormHelperText>
    </React.Fragment>
  );
};

class SignUpDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      consultationType: this.props.match.params.consultationType,
      errorMessage: "",
    };

    this.handleToggle = this.handleToggle.bind(this);

    if (!getVisitAPIToken()) {
      alert("Please sign in to continue");
      this.props.history.push({
        pathname: `/visits/${this.state.consultationType}/signup`,
      });
    }
  }

  validationSchema = Yup.object().shape({
    phone: Yup.string()
      .required("Phone Number is required.")
      .matches(phoneRegExStart0, "Phone number cannot start with 0")
      .matches(phoneRegExStart1, "Phone number cannot start with 1")
      .matches(phoneRegExAreaCode555, "Phone number cannot have 555 area code")
      .matches(phoneRegEx, "Enter a valid phone number"),
    gender: Yup.string().required("Gender is required."),
    profession: Yup.string().required("Please answer this question"),
    consent: Yup.boolean().oneOf([true], "Please agree to the consents"),
  });

  handleToggle(value) {
    const { checked } = this.state;
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    this.setState({
      checked: newChecked,
    });
  }

  renderBenefitHeadline() {
    var headline = "Let's Start Your Visit";
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
        headline = "One Step Closer To Confidently Cool";
        break;

      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        headline = "One Step Closer to Sleeping Soundly";
        break;
    }
    return headline;
  }

  render() {
    const { classes } = this.props;
    return (
      <div
        className={classes.container}
        style={{ backgroundColor: COLOR_KICK_BLUE }}
      >
        <GridContainer justifyContent="center">
          <GridItem xs={12} sm={12} md={10} lg={8}>
            <Card className={classes.cardSignup}>
              <Headline headline={this.renderBenefitHeadline()} />

              <CardBody style={{ padding: "0" }}>
                <GridContainer justifyContent="center">
                  <GridItem xs={12} sm={6} lg={5}>
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "12px",
                      }}
                    >
                      <Check
                        style={{
                          backgroundColor: kickBlue,
                          borderRadius: "100%",
                          color: "white",
                          fontSize: "13px",
                          padding: "2px",
                          marginRight: "8px",
                        }}
                        size="small"
                      />
                      <span style={{ fontWeight: "400" }}>
                        Our doctors are licensed & board certified
                      </span>
                    </p>

                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "0px",
                      }}
                    >
                      <Check
                        style={{
                          backgroundColor: kickBlue,
                          borderRadius: "100%",
                          color: "white",
                          fontSize: "13px",
                          padding: "2px",
                          marginRight: "8px",
                        }}
                        size="small"
                      />
                      <span style={{ fontWeight: "400" }}>
                        Your health information is securely stored
                      </span>
                    </p>
                    <Formik
                      validationSchema={this.validationSchema}
                      initialValues={initialValues}
                      onSubmit={(values, { setSubmitting }) => {
                        const { phone, gender, profession } = values;

                        const path = "/signup-details";
                        const data = {
                          phone: phone.trim(),
                          profession: profession.trim(),
                          gender: gender,
                          consultationType: this.state.consultationType,
                        };
                        KickClient.newAuthenticatedClient()
                          .put(path, data)
                          .then((response) => {
                            setSubmitting(false);

                            try {
                              trackConsultStart();
                            } catch (error) {
                              bugsnagClient.notify(error);
                              console.error("[/signup-details] Error: ", error);
                            }

                            setVisitGender(gender);
                            setVisitPhone(
                              phone.replace(/[-()]/g, "").replace(" ", ""),
                            );

                            trackSignup(
                              getVisitUSState(),
                              this.state.consultationType,
                            );

                            identifyKlaviyoPatientSignUp(
                              getVisitEmail(),
                              this.state.consultationType,
                            );

                            identifyCustomerIOPatientSignUp(
                              getVisitEmail(),
                              this.state.consultationType,
                            );

                            this.props.history.push({
                              pathname: `/visits/${this.state.consultationType}/1`,
                            });

                            window.scrollTo(0, 100);
                          })
                          .catch((error) => {
                            let errorMessage =
                              error.response?.data?.errors?.[0]?.title ||
                              "There was an error.";
                            if (
                              errorMessage ===
                              errorMessages.PHONE_NUMBER_ALREADY_EXISTS
                            ) {
                              errorMessage = () => (
                                <>
                                  <div
                                    style={{
                                      marginBottom: "6px",
                                      fontSize: "15px",
                                    }}
                                  >
                                    This phone number is already in use.
                                    <br />
                                    Check text messages on your phone for a link
                                    to login.
                                  </div>
                                  <a
                                    href="/me/login-send-sms"
                                    style={{ textDecoration: "underline" }}
                                  >
                                    Click here to resend the login link
                                  </a>
                                </>
                              );
                            }
                            this.setState({ errorMessage });
                            setSubmitting(false);
                            bugsnagClient.notify(error);
                            console.log("Error signing up: ", error);
                          });
                      }}
                    >
                      {(props) => (
                        <Form
                          className={classes.form}
                          style={{
                            marginTop: "24px",
                          }}
                        >
                          <Field
                            component={TextFieldGender}
                            style={{ width: "100%", minWidth: "250px" }}
                            type="text"
                            name="gender"
                            labeltext="Gender"
                            select
                            variant="outlined"
                            fullWidth
                          >
                            {genders.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Field>
                          <Field
                            id="profession"
                            name="profession"
                            labeltext="What kind of work do you do?"
                            placeholder="Describe your profession"
                            component={FormikTextField}
                            style={{ width: "100%", minWidth: "250px" }}
                            variant="outlined"
                            fullWidth
                          />

                          <Field
                            component={TextFieldPhoneNumber}
                            style={{ width: "100%", minWidth: "250px" }}
                            type="text"
                            name="phone"
                            labeltext="Phone Number"
                            variant="outlined"
                            fullWidth
                          />

                          <div className={classes.center}>
                            <Field
                              id="consent"
                              name="consent"
                              component={ConsentCheckbox}
                              type="checkbox"
                            />

                            <Button
                              disabled={props.isSubmitting}
                              onClick={props.submitForm}
                              className={classes.signupButton}
                            >
                              Complete Signup
                              {props.isSubmitting ? (
                                <CircularProgress
                                  size="large"
                                  style={{
                                    position: "relative",
                                    left: "24px",
                                    top: "1px",
                                    color: kickBlue,
                                  }}
                                />
                              ) : null}
                            </Button>

                            {this.state.errorMessage && (
                              <p style={{ color: "red", marginTop: "10px" }}>
                                {typeof this.state.errorMessage === "function"
                                  ? this.state.errorMessage()
                                  : this.state.errorMessage}
                              </p>
                            )}
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </GridItem>
                </GridContainer>
              </CardBody>

              <div
                style={{
                  width: "72%",
                  alignSelf: "center",
                }}
                className={classes.center}
              >
                <p style={{ color: "#777", marginTop: "12px" }}>
                  By clicking 'Complete Signup' I agree by electronic signature
                  to be contacted by Kick Health through a live agent,
                  artificial or prerecorded voice, and automated SMS text at my
                  residential or cellular number, dialed manually or by
                  autodialer, and by email. I understand I am not required to
                  agree to this as a condition to purchase
                </p>
              </div>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

SignUpDetails.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(consultationStyles)(SignUpDetails);
